import { OVERTIME, DOUBLE_TIME, OTHER, TIME_TYPES } from '@/lib/constants/timeTypes'
import { COMPLETE } from '@/lib/constants/actionStatuses'

// The three non-standard, billable time types that carry their own pay/bill rate pair on the
// assignment - ST is priced from the assignment's own rate, NB is non-billable.
export const PREMIUM_TIME_TYPE_IDS = [OVERTIME, DOUBLE_TIME, OTHER]

function isTimeTypeCatalogActive ({ timeTypesList, meta, timeTypeId }) {
  const timeType = (timeTypesList || []).find(type => type.id === timeTypeId)
  if (timeType) {
    return timeType.active
  }

  // The catalog fetch hasn't succeeded (still loading, or failed) - fail open so a
  // server-required rate pair isn't hidden by a transient load failure.
  return !meta || meta.status !== COMPLETE
}

export function findTimeTypeRate ({ timeTypeRates, timeTypeId }) {
  return (timeTypeRates || []).find(rate => rate.timeTypeId === timeTypeId)
}

// Builds one pay/bill rate row per active premium time type, generated from the time-type
// catalog rather than hardcoded per type. All three types are shown on the same terms
// (catalog-active, or already configured) - OT's pay/bill pair prices OT hours whether they
// come from a manually-tagged row or from automatic threshold-derivation (see
// TimeTypeBucketResolver), so it must stay visible and editable regardless of
// overtimeEligible. OT alone can be mandatory: the server rejects a save with
// overtimeEligible true and no configured OT rate pair (Assignments.EnsureOvertimeConfigured),
// since that pair is what prices the automatically-derived hours too.
export function buildPremiumRateRows ({ timeTypesList, timeTypeRates, meta, overtimeEligible }) {
  return PREMIUM_TIME_TYPE_IDS
    .map(timeTypeId => {
      const existingRate = findTimeTypeRate({ timeTypeRates, timeTypeId })
      // A deactivated type drops out of configuration choices for a fresh pick, but an
      // assignment that already has a rate configured for it keeps showing it.
      const visible = isTimeTypeCatalogActive({ timeTypesList, meta, timeTypeId }) || !!existingRate

      return {
        timeTypeId,
        code: TIME_TYPES[timeTypeId],
        required: timeTypeId === OVERTIME && overtimeEligible,
        payRate: existingRate ? existingRate.payRate : null,
        billRate: existingRate ? existingRate.billRate : null,
        visible
      }
    })
    .filter(row => row.visible)
}

// Returns a new timeTypeRates array with the given type's row merged with `changes`,
// preserving every other type's row untouched.
export function withTimeTypeRate ({ timeTypeRates, timeTypeId, changes }) {
  const otherRates = (timeTypeRates || []).filter(rate => rate.timeTypeId !== timeTypeId)
  const existingRate = findTimeTypeRate({ timeTypeRates, timeTypeId })
  const newRate = Object.assign({ id: 0, timeTypeId, payRate: null, billRate: null }, existingRate, changes)

  return [...otherRates, newRate]
}
